import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/PlanoContas_CloneInput.ts';
import '../contracts/Planocontas_CloneResponse';
import '../contracts/Planocontas_RecalcularInput';
import '../contracts/Planocontas_RecalcularResponse';
import '../contracts/Planocontas_ShowResponse';
import '../contracts/Planoscontas_CarregaPlanosResponse';
import type { Planoscontas_CarregaPlanosResponse } from '../contracts/Planoscontas_CarregaPlanosResponse';

import '../contracts/Planoscontas_CreateTemplateResponse';
import type { Planoscontas_CreateTemplateResponse } from '../contracts/Planoscontas_CreateTemplateResponse';
import '../contracts/Planoscontas_HistoryResponse';
import '../contracts/Planoscontas_RecalcularSnapshotInput';
import '../contracts/Planoscontas_SnapshotResponse';
import '../contracts/Planoscontas_TemplateResponse';
import '../contracts/Planoscontas_UpdatePercentualResponse';
import type { Planoscontas_UpdatePercentualResponse } from '../contracts/Planoscontas_UpdatePercentualResponse';
import '../contracts/Planoscontas_UpdateStatusResponse';
import '../contracts/Planoscontas_UpdateValorResponse';
import type { Planoscontas_UpdateValorResponse } from '../contracts/Planoscontas_UpdateValorResponse';
import type { PlanoContas_CloneInput } from '../contracts/PlanoContas_CloneInput';
/**
 * Composable for Plano de Contas (Budget Accounts) operations
 *
 * 4-Layer Architecture:
 * - Layer 1: config_despesas_categorias (categories)
 * - Layer 2: planos_de_contas + itens (mutable template)
 * - Layer 3: plano_contas_snapshots (immutable snapshots)
 * - Layer 4: despesas (linked via categoria_id only)
 *
 * TYPED DATA LAYER: Date handling follows the typed data layer pattern:
 * - DATE-ONLY fields: Use fromDateToApiDate (YYYY-MM-DD, no timezone conversion)
 * - DATETIME fields: Use toApiDateTimeFormat/fromApiDateTimeFormat (ISO 8601 UTC)
 */
export function usePlanoContas() {
  const isLoading = ref(false);
  const error = ref(null);

  // ===========================================
  // FILTER-BASED LISTING (follows useDespesas pattern)
  // ===========================================

  /**
   * Fetch ALL plans (active and inactive) for all franchises in the current session filter.
   * Uses the global filter (session-based) — no franchise ID parameter needed.
   * @returns {Array} List of all plans with franchise names
   */
  const fetchPlanos = async () => {
    log('usePlanoContas', 'fetchPlanos', 'Fetching all plans (active and inactive)');
    isLoading.value = true;
    error.value = null;
    try {
      const timestamp = new Date().getTime();
      const response = await httpService.get<Planoscontas_CarregaPlanosResponse>(
        `/index.php/planoscontas/carrega_planos?include_all=1&_t=${timestamp}`,
        { responseContract: 'Planoscontas_CarregaPlanosResponse' }
      );
      log('usePlanoContas', 'fetchPlanos', 'Response', response);
      if (response.success) {
        return response.dados?.planos ?? [];
      } else {
        throw new Error(response.error || 'Erro ao buscar planos');
      }
    } catch (err) {
      log('usePlanoContas', 'fetchPlanos', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Toggle plan status (active/inactive).
   * When activating, this will deactivate other plans for the same franchise.
   * @param {number} planoId - Plan ID to toggle
   * @param {boolean} newStatus - New status (true = active)
   */
  const togglePlanStatus = async (planoId: number, newStatus: string) => {
    log('usePlanoContas', 'togglePlanStatus', 'Toggling status', { planoId, newStatus });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.put(
        `/index.php/planoscontas/update_status/${planoId}`,
        { status: newStatus ? 'active' : 'inactive' },
        { responseContract: 'Planoscontas_UpdateStatusResponse', inputContract: 'Planoscontas_UpdateStatusInput' }
      );
      log('usePlanoContas', 'togglePlanStatus', 'Response', response);
      if (response.success) {
        return { data: response.dados, message: response.dados?.message };
      } else {
        throw new Error(response.error || 'Erro ao atualizar status');
      }
    } catch (err) {
      log('usePlanoContas', 'togglePlanStatus', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================
  // TEMPLATE METHODS (mutable, affects future)
  // ===========================================

  /**
   * Fetch active template for a franchise
   * @param {number} franquiaId - Franchise ID
   */
  const fetchTemplate = async (franquiaId: number) => {
    log('usePlanoContas', 'fetchTemplate', 'Fetching template', { franquiaId });
    isLoading.value = true;
    error.value = null;

    try {
      const timestamp = new Date().getTime();
      const response = await httpService.get(
        `/index.php/planoscontas/template/${franquiaId}?_t=${timestamp}`,
        { responseContract: 'Planoscontas_TemplateResponse' }
      );
      log('usePlanoContas', 'fetchTemplate', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar template');
      }
    } catch (err) {
      log('usePlanoContas', 'fetchTemplate', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update valor_previsto for a template item (fixed account)
   * @param {number} itemId - Item ID
   * @param {number} valor - New value
   */
  const updateTemplateItemValor = async (itemId: number, valor: number) => {
    log('usePlanoContas', 'updateTemplateItemValor', 'Updating valor', { itemId, valor });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.put<Planoscontas_UpdateValorResponse>(
        `/index.php/planoscontas/template/item/${itemId}/valor`,
        { valor },
        { responseContract: 'Planoscontas_UpdateValorResponse', inputContract: 'Planoscontas_UpdateValorInput' }
      );
      log('usePlanoContas', 'updateTemplateItemValor', 'Response', response);

      if (response.success) {
        return { data: response.dados, message: response.dados?.message };
      } else {
        throw new Error(response.error || 'Erro ao atualizar valor');
      }
    } catch (err) {
      log('usePlanoContas', 'updateTemplateItemValor', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update percentual for a template item (variable account)
   * @param {number} itemId - Item ID
   * @param {number} percentual - New percentage (0-100)
   */
  const updateTemplateItemPercentual = async (itemId: number, percentual: number) => {
    log('usePlanoContas', 'updateTemplateItemPercentual', 'Updating percentual', { itemId, percentual });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.put<Planoscontas_UpdatePercentualResponse>(
        `/index.php/planoscontas/template/item/${itemId}/percentual`,
        { percentual },
        { responseContract: 'Planoscontas_UpdatePercentualResponse', inputContract: 'Planoscontas_UpdatePercentualInput' }
      );
      log('usePlanoContas', 'updateTemplateItemPercentual', 'Response', response);

      if (response.success) {
        return { data: response.dados, message: response.dados?.message };
      } else {
        throw new Error(response.error || 'Erro ao atualizar percentual');
      }
    } catch (err) {
      log('usePlanoContas', 'updateTemplateItemPercentual', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create new template version
   * @param {number} franquiaId - Franchise ID
   * @param {string} nome - Plan name (optional)
   * @param {Array} itens - Category configurations: [{ categoria_id, tipo, valor_previsto, percentual_custom }]
   */
  const createTemplateVersion = async (franquiaId: number, nome: string | null = null, itens: unknown[] = []) => {
    log('usePlanoContas', 'createTemplateVersion', 'Creating version', { franquiaId, nome, itens });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<Planoscontas_CreateTemplateResponse>('/index.php/planoscontas/template', {
        franquia_id: franquiaId,
        nome,
        itens
      }, { responseContract: 'Planoscontas_CreateTemplateResponse', inputContract: 'Planoscontas_CreateTemplateInput' });
      log('usePlanoContas', 'createTemplateVersion', 'Response', response);

      if (response.success) {
        return { data: response.dados, message: response.dados?.message };
      } else {
        throw new Error(response.error || 'Erro ao criar novo plano');
      }
    } catch (err) {
      log('usePlanoContas', 'createTemplateVersion', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get plan version history for a franchise
   * @param {number} franquiaId - Franchise ID
   */
  const fetchPlanHistory = async (franquiaId: number) => {
    log('usePlanoContas', 'fetchPlanHistory', 'Fetching history', { franquiaId });
    isLoading.value = true;
    error.value = null;

    try {
      const timestamp = new Date().getTime();
      const response = await httpService.get(
        `/index.php/planoscontas/history/${franquiaId}?_t=${timestamp}`,
        { responseContract: 'Planoscontas_HistoryResponse' }
      );
      log('usePlanoContas', 'fetchPlanHistory', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar histórico');
      }
    } catch (err) {
      log('usePlanoContas', 'fetchPlanHistory', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================
  // SNAPSHOT METHODS (immutable, historical)
  // ===========================================

  /**
   * Fetch snapshot for a fechamento (immutable values)
   * @param {number} fechamentoId - Fechamento ID
   */
  const fetchSnapshot = async (fechamentoId: number) => {
    log('usePlanoContas', 'fetchSnapshot', 'Fetching snapshot', { fechamentoId });
    isLoading.value = true;
    error.value = null;

    try {
      const timestamp = new Date().getTime();
      const response = await httpService.get(
        `/index.php/planoscontas/snapshot/${fechamentoId}?_t=${timestamp}`,
        { responseContract: 'Planoscontas_SnapshotResponse' }
      );
      log('usePlanoContas', 'fetchSnapshot', 'Response', response);

      if (response.success && response.dados) {
        return {
          contas: response.dados.contas,
          totais: response.dados.totais,
          fechamento: response.dados.fechamento
        };
      } else {
        throw new Error(response.error || 'Erro ao buscar snapshot');
      }
    } catch (err) {
      log('usePlanoContas', 'fetchSnapshot', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Recalculate valor_efetivo for a snapshot
   * @param {number} fechamentoId - Fechamento ID
   */
  const recalculateSnapshot = async (fechamentoId: number) => {
    log('usePlanoContas', 'recalculateSnapshot', 'Recalculating', { fechamentoId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(
        `/index.php/planoscontas/snapshot/${fechamentoId}/recalcular`,
        null, { inputContract: 'Planoscontas_RecalcularSnapshotInput', responseContract: 'Planoscontas_SnapshotResponse' }
      );
      log('usePlanoContas', 'recalculateSnapshot', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao recalcular');
      }
    } catch (err) {
      log('usePlanoContas', 'recalculateSnapshot', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================
  // LEGACY METHODS (backward compatibility)
  // ===========================================

  /**
   * Fetch all contas for a fechamento (uses snapshots internally)
   * @deprecated Use fetchSnapshot() instead
   * @param {number} fechamentoId - Fechamento ID
   * @param {Object} filters - Optional filters
   */
  const fetchContasByFechamento = async (fechamentoId: any, filters: any = {}) => {
    log('usePlanoContas', 'fetchContasByFechamento', '[DEPRECATED] Use fetchSnapshot instead');

    // Forward to new snapshot method
    const result = await fetchSnapshot(fechamentoId);
    return result;
  };

  /**
   * Fetch single conta by ID
   * @param {number} id - Conta ID
   */
  const fetchConta = async (id: any) => {
    log('usePlanoContas', 'fetchConta', 'Fetching conta', id);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/planocontas/show/${id}`, {
        responseContract: 'Planocontas_ShowResponse',
      });
      log('usePlanoContas', 'fetchConta', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar conta');
      }
    } catch (err) {
      log('usePlanoContas', 'fetchConta', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update valor_previsto for a fixed account
   * @deprecated Use updateTemplateItemValor() instead
   * @param {number} id - Conta ID
   * @param {number} valor - New value
   */
  const updateValor = async (id: any, valor: any) => {
    log('usePlanoContas', 'updateValor', '[DEPRECATED] Use updateTemplateItemValor instead');
    return updateTemplateItemValor(id, valor);
  };

  /**
   * Update percentual for a variable account
   * @deprecated Use updateTemplateItemPercentual() instead
   * @param {number} id - Conta ID
   * @param {number} percentual - New percentage (0-100)
   */
  const updatePercentual = async (id: any, percentual: any) => {
    log('usePlanoContas', 'updatePercentual', '[DEPRECATED] Use updateTemplateItemPercentual instead');
    return updateTemplateItemPercentual(id, percentual);
  };

  /**
   * Recalculate valor_efetivo for a conta
   * @deprecated Use recalculateSnapshot() instead
   * @param {number} id - Conta ID
   */
  const recalcularConta = async (id: number) => {
    log('usePlanoContas', 'recalcularConta', '[DEPRECATED] Use recalculateSnapshot instead');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/planocontas/recalcular/${id}`, null, {
        inputContract: 'Planocontas_RecalcularInput',
        responseContract: 'Planocontas_RecalcularResponse',
      });
      log('usePlanoContas', 'recalcularConta', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao recalcular');
      }
    } catch (err) {
      log('usePlanoContas', 'recalcularConta', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================
  // HELPER METHODS
  // ===========================================

  /**
   * Get variance status label
   * @param {number} variacaoPercentual - Variance percentage
   */
  const getVarianceStatus = (variacaoPercentual: any) => {
    if (variacaoPercentual <= 100) return 'ok';
    if (variacaoPercentual <= 110) return 'warning';
    return 'exceeded';
  };

  /**
   * Get variance icon
   * @param {number} variacaoPercentual - Variance percentage
   */
  const getVarianceIcon = (variacaoPercentual: any) => {
    const status = getVarianceStatus(variacaoPercentual);
    switch (status) {
      case 'ok':
        return '🟢';
      case 'warning':
        return '🟡';
      case 'exceeded':
        return '🔴';
      default:
        return '⚪';
    }
  };

  /**
   * Get variance color for styling
   * @param {number} variacaoPercentual - Variance percentage
   */
  const getVarianceColor = (variacaoPercentual: any) => {
    const status = getVarianceStatus(variacaoPercentual);
    switch (status) {
      case 'ok':
        return 'success';
      case 'warning':
        return 'warning';
      case 'exceeded':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  /**
   * Export plano de contas to CSV
   * @param {number} fechamentoId - Fechamento ID
   */
  const exportToCSV = async (fechamentoId: number) => {
    log('usePlanoContas', 'exportToCSV', 'Exporting to CSV', { fechamentoId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(
        `/index.php/planocontas/export/${fechamentoId}`,
        { responseType: 'blob' } as any
      );
      log('usePlanoContas', 'exportToCSV', 'Response', response);

      if (response.success || response instanceof Blob) {
        // Create download link
        const blob = response instanceof Blob ? response : new Blob([response.dados as BlobPart]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `plano-contas-${fechamentoId}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        return true;
      } else {
        throw new Error(response.error || 'Erro ao exportar CSV');
      }
    } catch (err) {
      log('usePlanoContas', 'exportToCSV', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Clone plano de contas from previous fechamento
   * @param {number} fromFechamentoId - Source fechamento ID
   * @param {number} toFechamentoId - Target fechamento ID
   */
  const cloneFromPrevious = async (fromFechamentoId: any, toFechamentoId: any) => {
    log('usePlanoContas', 'cloneFromPrevious', 'Cloning plano', { fromFechamentoId, toFechamentoId });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: PlanoContas_CloneInput = {
        from_fechamento_id: fromFechamentoId,
        to_fechamento_id: toFechamentoId
      };
      validate('PlanoContas_CloneInput', payload);
      const response = await httpService.post(
        `/index.php/planocontas/clone`,
        payload,
        { responseContract: 'Planocontas_CloneResponse' }
      );
      log('usePlanoContas', 'cloneFromPrevious', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao clonar plano de contas');
      }
    } catch (err) {
      log('usePlanoContas', 'cloneFromPrevious', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,

    // Filter-based listing
    fetchPlanos,
    togglePlanStatus,

    // NEW: Template methods (4-layer architecture)
    fetchTemplate,
    updateTemplateItemValor,
    updateTemplateItemPercentual,
    createTemplateVersion,
    fetchPlanHistory,

    // NEW: Snapshot methods (immutable)
    fetchSnapshot,
    recalculateSnapshot,

    // NEW: Export and Clone
    exportToCSV,
    cloneFromPrevious,

    // LEGACY: Backward compatibility
    fetchContasByFechamento,
    fetchConta,
    updateValor,
    updatePercentual,
    recalcularConta,

    // Helpers
    getVarianceStatus,
    getVarianceIcon,
    getVarianceColor
  };
}
